// @ts-check

/**
 * @file Renders the task detail route.
 */

import {FlashNotifications} from "flash-notifications"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import Text from "@kaspernj/api-maker/build/utils/text"
import {memo, useMemo} from "react"
import {View} from "react-native-propforge"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"
import AddDailyWorkModal from "../../../src/screens/tasks/add-daily-work-modal"
import ActionMenu from "../../../src/components/action-menu"
import Button from "../../../src/components/button"
import Card from "../../../src/components/card"
import Link from "../../../src/components/link"
import SharedRenderHtml from "../../../src/components/render-html"
import TaskDetailRow from "../../../src/screens/tasks/detail-row"
import TaskBoardPlacements from "../../../src/components/tasks/board-placements"
import TaskSubnav from "../../../src/screens/tasks/subnav"
import buildRoutePath from "../../../src/shared/build-route-path"
import confirmModal from "../../../src/shared/confirm-modal"
import errorMessage from "../../../src/shared/error-message"
import {Comment as RemoteComment} from "../../../src/frontend-models/comment"
import {Task as RemoteTask} from "../../../src/frontend-models/task"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {canRespondToAutomationPlan, translatedAgentState, translatedAutomationRuntimeStatus} from "../../../src/shared/agent-state"
import {translatedTaskState} from "../../../src/shared/task-state"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"
import {parseDurationSeconds} from "../../../src/shared/duration"
import {projectReplicaRowsSource} from "../../../src/database/project-replica-reads"
import {queueOfflineTaskDelete} from "../../../src/database/project-replica-task-commands"
import Project from "../../../src/models/project"
import TaskAssignedUser from "../../../src/models/task-assigned-user"
import TaskType from "../../../src/models/task-type"
import Task from "../../../src/models/task"
import Comment from "../../../src/models/comment"
import Timelog from "../../../src/models/timelog"
import User from "../../../src/models/user"
import useCurrentUser from "../../../src/auth/use-current-user"
import {useProjectSyncOnline} from "../../../src/sync/project-sync-connectivity"
import {useActiveProjectReplica} from "../../../src/sync/project-sync-lifecycle"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {ActionMenuItem} from "../../../src/components/action-menu" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Formats a task owner display name.
 * @param {InstanceType<typeof User> | null} user User to display.
 * @returns {string} Best available display name.
 */
function userDisplayName(user) {
  if (!user) return "-"
  const firstName = user.firstName() || ""
  const lastName = user.lastName() || ""
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || user.email() || "-"
}

/** @typedef {{[key: string]: unknown}} TasksShowScreenProps */

/**
 * @typedef {object} TasksShowScreenState
 * @property {boolean} approvingAutomationPlan Whether an automation approval comment is being created.
 * @property {boolean} closing Whether a close request is running.
 * @property {string} dailyWorkErrorText Daily-work modal validation or save error.
 * @property {boolean} dailyWorkFormOpen Whether the daily work form is visible.
 * @property {boolean} dailyWorkSaving Whether daily work is saving.
 * @property {boolean} deleting Whether a delete request is running.
 * @property {boolean} showActionsMenu Whether the task actions menu is visible.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 */

/**
 * Shows task details and task actions.
 * @augments {ShapeComponent<TasksShowScreenProps, TasksShowScreenState>}
 */
class TasksShowScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params = this.hookField()

  /** @type {ReturnType<typeof useRouter>} */
  router = this.hookField()

  /** @type {string} */
  taskID = ""

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {string} */
  projectID = ""

  /** @type {InstanceType<typeof Task> | null} */
  task = null

  /** @type {InstanceType<typeof Project> | null} */
  project = null

  /** @type {InstanceType<typeof TaskType> | null} */
  taskType = null

  /** @type {InstanceType<typeof User> | null} */
  owner = null

  assignedUsersCount = 0
  commentsCount = 0
  timelogsCount = 0
  loading = true
  online = true
  readError = /** @type {Error | null} */ (null)
  canClose = false

  /** @type {TasksShowScreenState} */
  state = {
    approvingAutomationPlan: false,
    closing: false,
    dailyWorkErrorText: "",
    dailyWorkFormOpen: false,
    dailyWorkSaving: false,
    deleting: false,
    showActionsMenu: false,
    status: /** @type {{kind: "error", text: string} | null} */ null
  }

  /** Declares task-detail reads against the exact route project replica. */
  setup() {
    useLocale()
    const params = useLocalSearchParams()
    const projectID = requiredRouteParam(params, "projectId", "Project ID")
    const taskID = requiredRouteParam(params, "id", "Task ID")
    const replica = useActiveProjectReplica(projectID)
    const taskSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Task,
      projectId: projectID,
      query: (tasks) => tasks.where({id: taskID, projectId: projectID}).limit(1),
      replica
    }), [projectID, replica, taskID])
    const assignedUsersSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: TaskAssignedUser,
      projectId: projectID,
      query: (assignments) => assignments.where({taskId: taskID}),
      replica
    }), [projectID, replica, taskID])
    const commentsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Comment,
      projectId: projectID,
      query: (comments) => comments.where({resourceId: taskID, resourceType: "Task"}),
      replica
    }), [projectID, replica, taskID])
    const timelogsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: Timelog,
      projectId: projectID,
      query: (timelogs) => timelogs.where({taskId: taskID}),
      replica
    }), [projectID, replica, taskID])
    const taskState = useLiveQuery(taskSource)
    const assignedUsersState = useLiveQuery(assignedUsersSource)
    const commentsState = useLiveQuery(commentsSource)
    const timelogsState = useLiveQuery(timelogsSource)
    const task = taskState.results[0] || null
    const taskTypeId = task?.taskTypeId() || ""
    const ownerId = task?.userId() || ""
    const taskTypeSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: TaskType,
      projectId: projectID,
      query: (taskTypes) => taskTypeId ? taskTypes.where({id: taskTypeId}).limit(1) : taskTypes.where({id: []}),
      replica
    }), [projectID, replica, taskTypeId])
    const projectQuery = useMemo(() => Project.where({id: projectID}).limit(1), [projectID])
    const ownerQuery = useMemo(() => ownerId ? User.where({id: ownerId}).limit(1) : User.where({id: []}), [ownerId])
    const taskTypeState = useLiveQuery(taskTypeSource)
    const projectState = useLiveQuery(projectQuery)
    const ownerState = useLiveQuery(ownerQuery)
    const queryStates = [taskState, assignedUsersState, commentsState, timelogsState, taskTypeState, projectState, ownerState]
    const {user: currentUser} = useCurrentUser()
    const loading = !replica || queryStates.some((state) => state.loading)

    this.params = params
    this.router = useRouter()
    this.taskID = taskID
    this.projectID = projectID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.online = useProjectSyncOnline()
    this.replica = replica
    this.task = task
    this.project = projectState.results[0] || null
    this.taskType = taskTypeState.results[0] || null
    this.owner = ownerState.results[0] || null
    this.assignedUsersCount = assignedUsersState.results.length
    this.commentsCount = commentsState.results.length
    this.timelogsCount = timelogsState.results.length
    this.canClose = Boolean(currentUser?.admin && task?.state() === "open")
    this.loading = loading
    this.readError = queryStates.find((state) => state.error)?.error || null
    useScreenLoading(loading)
  }

  /**
   * Renders the task detail screen.
   * @returns {ReactNode} Task detail content.
   */
  render() {
    const {loading, owner, project, task, taskType} = this
    const {params, projectID, taskID} = this.tt
    const status = this.s.status || (this.readError ? {kind: "error", text: this.readError.message} : null)
    const screenTitle = task ? task.name() : _("Task")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="taskShowScreen"
        />
      )
    }
    const projectName = project?.name() || "-"
    const description = task?.description() || ""
    const automationRuntimeStatus = task ? translatedAutomationRuntimeStatus(task.automationRuntimeStatus()) : null
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="taskShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="taskShowScreen/content"
        >
          {task &&
            <TaskSubnav
              activeItem="general"
              assignedUsersCount={this.assignedUsersCount}
              commentsCount={this.commentsCount}
              params={params}
              projectId={projectID}
              taskId={taskID}
              testID="taskShowScreen/subnav"
              timelogsCount={this.timelogsCount}
            />
          }
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="taskShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {task &&
            <>
              <Card
                style={styles.cardSpacing ||= {padding: 20}}
                testID="taskShowScreen/card"
              >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="taskShowScreen/headerRow"
              >
                <Text
                  style={styles[`headerTitle-${this.tt.themeName}`] ||= {
                    fontSize: 24,
                    fontWeight: 700
                  }}
                  testID="taskShowScreen/title"
                >
                  {task.name()}
                </Text>
                <View
                  style={styles.headerActions ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 8
                  }}
                  testID="taskShowScreen/headerActions"
                >
                  <Button
                    disabled={this.s.showActionsMenu}
                    iconName="ellipsis"
                    label={_("Actions")}
                    onPress={this.tt.onActionsButtonPress}
                    testID="taskShowScreen/actionsButton"
                  />
                </View>
              </View>
              <TaskDetailRow
                label={_("Name")}
                testID="name"
                value={task.name() || "-"}
              />
              <TaskDetailRow
                label={_("State")}
                testID="state"
                value={translatedTaskState(task.state())}
              />
              <TaskDetailRow
                label={_("Agent state")}
                testID="agentState"
                value={translatedAgentState(task.agentState())}
              />
              {automationRuntimeStatus &&
                <TaskDetailRow
                  label={_("Automation status")}
                  testID="automationRuntimeStatus"
                  value={automationRuntimeStatus}
                />
              }
              <TaskDetailRow
                label={_("Priority")}
                testID="priority"
                value={task.priority() == null ? "-" : `${task.priority()}`}
              />
              <View
                collapsable={false}
                style={styles[`detailRow-${this.tt.themeName}`] ||= {
                  borderBottomColor: this.tt.colors.divider,
                  borderBottomWidth: 1,
                  paddingVertical: 10
                }}
                testID="taskShowScreen/description/row"
              >
                <Text
                  style={styles[`detailLabel-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.textMuted,
                    fontWeight: 600,
                    marginBottom: 4
                  }}
                  testID="taskShowScreen/description/label"
                >
                  {_("Description")}
                </Text>
                <View
                  accessible
                  accessibilityLabel={description || "-"}
                  collapsable={false}
                  style={styles.descriptionValue ||= {
                    paddingVertical: 4
                  }}
                  testID="taskShowScreen/description/value"
                >
                  {description
                    ? <SharedRenderHtml source={description} />
                    : <Text style={styles[`detailValue-${this.tt.themeName}`] ||= {fontSize: 16}}>-</Text>
                  }
                </View>
              </View>
              <View
                collapsable={false}
                style={styles[`detailRow-${this.tt.themeName}`]}
                testID="taskShowScreen/project/row"
              >
                <Text
                  style={styles[`detailLabel-${this.tt.themeName}`]}
                  testID="taskShowScreen/project/label"
                >
                  {_("Project")}
                </Text>
                {projectID ? (
                  <Link
                    accessibilityLabel={projectName}
                    href={buildRoutePath(params, `/projects/${projectID}`)}
                    style={styles[`detailLink-${this.tt.themeName}`] ||= {
                      alignSelf: "flex-start",
                      color: this.tt.colors.link,
                      fontSize: 16,
                      fontWeight: 600
                    }}
                    testID="taskShowScreen/project/link"
                  >
                    {projectName}
                  </Link>
                ) : (
                  <Text
                    accessible
                    accessibilityLabel="-"
                    style={styles[`detailValue-${this.tt.themeName}`]}
                    testID="taskShowScreen/project/value"
                  >
                    -
                  </Text>
                )}
              </View>
              <TaskDetailRow
                label={_("Task type")}
                testID="taskType"
                value={taskType?.name() || "-"}
              />
              <TaskDetailRow
                label={_("Owner")}
                testID="owner"
                value={userDisplayName(owner)}
              />
              </Card>
              <TaskBoardPlacements
                online={this.online}
                params={params}
                projectId={projectID}
                task={task}
                testID="taskShowScreen/boardPlacements"
              />
            </>
          }
        </View>
        {task &&
          <ActionMenu
            isVisible={this.s.showActionsMenu}
            items={this.tt.taskActionMenuItems(task, params, taskID, projectID)}
            onClosePress={this.tt.onActionsMenuClosePress}
            testID="taskShowScreen/actionsMenu"
            title={_("Task actions")}
          />
        }
        <AddDailyWorkModal
          errorText={this.s.dailyWorkErrorText}
          isVisible={this.s.dailyWorkFormOpen}
          onRequestClose={this.tt.onAddDailyWorkRequestClose}
          onSubmit={this.tt.onAddDailyWorkSubmit}
          saving={this.s.dailyWorkSaving}
        />
      </View>
    )
  }

  /**
   * Builds the actions shown in the task action menu.
   * @param {InstanceType<typeof Task>} task Loaded local task.
   * @param {ReturnType<typeof useLocalSearchParams>} params Current route params.
   * @param {string} taskID Current task id.
   * @param {string} projectID Current project id.
   * @returns {ActionMenuItem[]} Menu item config.
   */
  taskActionMenuItems(task, params, taskID, projectID) {
    const items = []
    const commentResourceParams = `resourceType=Task&resourceId=${taskID}&projectId=${encodeURIComponent(projectID)}`

    if (canRespondToAutomationPlan({agentState: task.agentState(), runtimeStatus: task.automationRuntimeStatus()})) {
      items.push(
        {
          disabled: !this.online || this.s.approvingAutomationPlan,
          iconName: "check",
          label: this.s.approvingAutomationPlan ? _("Approving...") : _("Approve automation plan"),
          onPress: this.tt.onApproveAutomationPlanMenuItemPress,
          testID: "taskShowScreen/approveAutomationPlanButton"
        },
        {
          disabled: !this.online,
          href: buildRoutePath(params, `/comments/new?${commentResourceParams}&defaultComment=${encodeURIComponent("/changes ")}`),
          iconName: "message",
          label: _("Request automation changes"),
          onPress: this.tt.onActionsMenuClosePress,
          testID: "taskShowScreen/requestAutomationChangesButton"
        },
        {
          destructive: true,
          disabled: !this.online,
          href: buildRoutePath(params, `/comments/new?${commentResourceParams}&defaultComment=${encodeURIComponent("/cancel ")}`),
          iconName: "ban",
          label: _("Cancel automation run"),
          onPress: this.tt.onActionsMenuClosePress,
          testID: "taskShowScreen/cancelAutomationRunButton"
        }
      )
    }

    if (this.canClose) {
      items.push({
        disabled: !this.online || this.s.closing,
        iconName: "circle-xmark",
        label: this.s.closing ? _("Closing...") : _("Close"),
        onPress: this.tt.onCloseMenuItemPress,
        testID: "taskShowScreen/closeButton"
      })
    }

    items.push(
      {
        disabled: !this.online || this.s.dailyWorkFormOpen,
        iconName: "clock",
        label: _("Add daily work"),
        onPress: this.tt.onAddDailyWorkMenuItemPress,
        testID: "taskShowScreen/addDailyWorkButton"
      },
      {
        disabled: !this.online,
        href: buildRoutePath(
          params,
          projectID ? `/tasks/new?projectId=${projectID}` : "/tasks/new"
        ),
        iconName: "plus",
        label: _("Add task"),
        onPress: this.tt.onActionsMenuClosePress,
        testID: "taskShowScreen/addTaskButton"
      },
      {
        disabled: !this.online,
        href: buildRoutePath(params, `/tasks/${taskID}/edit?projectId=${encodeURIComponent(projectID || "")}`),
        iconName: "pen-to-square",
        label: _("Edit"),
        onPress: this.tt.onActionsMenuClosePress,
        testID: "taskShowScreen/editButton"
      },
      {
        destructive: true,
        disabled: this.s.deleting,
        iconName: "trash",
        label: this.s.deleting ? _("Deleting...") : _("Delete"),
        onPress: this.tt.onDeletePress,
        testID: "taskShowScreen/deleteButton"
      }
    )

    return items
  }

  /** Opens the task actions menu. */
  onActionsButtonPress = () => {
    this.s.showActionsMenu = true
  }

  /** Closes the task actions menu. */
  onActionsMenuClosePress = () => {
    this.s.showActionsMenu = false
  }

  /** Opens the add-daily-work form from the task actions menu. */
  onAddDailyWorkMenuItemPress = () => {
    this.s.showActionsMenu = false
    this.tt.onAddDailyWorkPress()
  }

  /**
   * Posts the online owner-decision command and waits for SyncClient convergence.
   * @returns {Promise<void>} Resolves after the command completes.
   * @rejects {unknown} When the command fails or the task is unavailable.
   */
  onApproveAutomationPlanMenuItemPress = async () => {
    this.assertOnline()
    const task = this.task
    if (!task) throw new Error(_("Task not found"))
    this.s.approvingAutomationPlan = true
    this.s.showActionsMenu = false
    this.s.status = null
    try {
      await RemoteComment.create({
        comment: "/approve",
        resourceId: task.id(),
        resourceType: "Task"
      })
      FlashNotifications.success(_("The automation plan was approved"))
    } catch (error) {
      this.s.status = {kind: "error", text: errorMessage(error)}
      FlashNotifications.errorResponse(error)
      throw error
    } finally {
      this.s.approvingAutomationPlan = false
    }
  }

  /** Opens the add-daily-work form. */
  onAddDailyWorkPress = () => {
    this.s.dailyWorkErrorText = ""
    this.s.dailyWorkFormOpen = true
  }

  /** Closes the add-daily-work modal unless a save is running. */
  onAddDailyWorkRequestClose = () => {
    if (this.s.dailyWorkSaving) return
    this.s.dailyWorkErrorText = ""
    this.s.dailyWorkFormOpen = false
  }

  /**
   * Saves the add-daily-work form.
   * @param {Record<string, unknown>} values Submitted form values.
   * @returns {Promise<void>} Resolves when save handling finishes.
   * @rejects {unknown} When saving daily work fails.
   */
  onAddDailyWorkSubmit = async (values) => {
    this.assertOnline()
    if (this.s.dailyWorkSaving) {
      // Ignore repeated keyboard submits while the current save is in flight.
      return
    }

    const task = this.task
    if (!task) throw new Error(_("Task not found"))
    const hoursValue = typeof values.hours === "string" ? values.hours.trim() : ""
    const timeSeconds = parseDurationSeconds(hoursValue)
    if (!timeSeconds || timeSeconds <= 0) {
      this.s.dailyWorkErrorText = _("Enter a duration like 1h 5m, 30s or 1.5")
      return
    }
    const commentValue = typeof values.comment === "string" ? values.comment : ""
    this.s.dailyWorkSaving = true
    this.s.dailyWorkErrorText = ""
    this.s.status = null
    try {
      const commandTask = remoteTaskCommand(task.id())
      await commandTask.addDailyWork({
        timelog: {
          comment: commentValue,
          timeSeconds
        }
      })
      this.s.dailyWorkErrorText = ""
      this.s.dailyWorkFormOpen = false
      FlashNotifications.success(_("The work was saved"))
    } catch (error) {
      const saveError = errorMessage(error)
      this.s.dailyWorkErrorText = saveError
      this.s.status = {
        kind: "error",
        text: saveError
      }
      FlashNotifications.errorResponse(error)
      throw error
    } finally {
      this.s.dailyWorkSaving = false
    }
  }

  /**
   * Confirms and closes the task.
   * @returns {Promise<void>} Resolves when close handling finishes.
   * @rejects {unknown} When closing the task fails.
   */
  onClosePress = async () => {
    this.assertOnline()
    const task = this.task
    if (!task) throw new Error(_("Task not found"))
    const ok = await confirmModal(
      _("Are you sure that you want to close this task?"),
      {
        cancelLabel: _("Cancel"),
        confirmLabel: _("Close task"),
        title: _("Close task"),
        tone: "destructive"
      }
    )
    if (!ok) return
    this.s.closing = true
    this.s.status = null
    try {
      await remoteTaskCommand(task.id()).close()
      FlashNotifications.success(_("The task was closed"))
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      FlashNotifications.errorResponse(error)
      throw error
    } finally {
      this.s.closing = false
    }
  }

  /**
   * Confirms and closes the task from the task actions menu.
   * @returns {Promise<void>} Resolves when close handling finishes.
   */
  onCloseMenuItemPress = async () => {
    this.s.showActionsMenu = false
    await this.tt.onClosePress()
  }

  /**
   * Confirms and deletes the task.
   * @returns {Promise<void>} Resolves when delete handling finishes.
   * @rejects {unknown} When deleting the task fails.
   */
  onDeletePress = async () => {
    const {params, router} = this.tt
    const task = this.task

    if (!task) throw new Error(_("Task not found"))

    this.s.showActionsMenu = false
    const ok = await confirmModal(
      _("Delete task: %{name}?", {
        name: task.name() ?? ""
      }),
      {
        cancelLabel: _("Cancel"),
        confirmLabel: _("Delete"),
        title: _("Confirm delete"),
        tone: "destructive"
      }
    )
    if (!ok) return
    this.s.deleting = true
    this.s.status = null

    try {
      if (this.online) {
        await remoteTaskCommand(task.id()).destroy()
        FlashNotifications.success(_("Task deleted"))
      } else {
        if (!this.replica) throw new Error(_("This task can't be deleted until the project is available."))
        await queueOfflineTaskDelete({replica: this.replica, taskId: task.id()})
        FlashNotifications.success(_("Task deleted offline"))
      }
      router.replace(buildRoutePath(params, "/tasks"))
    } catch (error) {
      const message = errorMessage(error)

      FlashNotifications.error(message)
      this.s.status = {
        kind: "error",
        text: message
      }
      throw error
    } finally {
      this.s.deleting = false
    }
  }

  /**
   * Fails visibly before an unsupported offline command can be created.
   * @returns {void} Nothing when commands are allowed.
   * @throws {Error} When the app is offline.
   */
  assertOnline() {
    if (this.online) return
    const message = _("This action is only available while online")
    this.s.status = {kind: "error", text: message}
    FlashNotifications.error(message)
    throw new Error(message)
  }
}

/** @type {NamedExoticComponent<TasksShowScreenProps>} */
const TasksShowScreenComponent = memo(shapeComponent(TasksShowScreen))

export default TasksShowScreenComponent

/**
 * Creates an online-only command instance from a durable local id.
 * @param {string} taskId Durable task id.
 * @returns {RemoteTask} Remote command instance.
 */
function remoteTaskCommand(taskId) {
  return RemoteTask.instantiateFromResponse({id: taskId})
}
